import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Truck,
  Package,
  MapPin,
  Tags,
  Save,
  Printer,
  AlertCircle,
  Check,
  Ban,
  ScanLine,
  ChevronRight,
  RefreshCw,
  X as XIcon,
  CheckCircle2,
  ShieldCheck,
  FileText,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { dispatchService } from '@/services/dispatch';
import type { ServiceableCourier } from '@/services/dispatch';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { printHtmlInWindow } from '@/lib/print';
import type {
  DispatchOrder,
  DispatchOrderItem,
  DispatchStatus,
  DispatchStatusEvent,
  TrackingScan,
} from '@/types/dispatch';

// Statuses at or past physical collection — once here, any lingering
// "pickup not scheduled" warning is stale (the courier clearly came).
const COLLECTED_STATES = new Set<DispatchStatus>([
  'PICKED',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'RETURN_INITIATED',
  'RETURNED',
]);

interface Props {
  dispatch: DispatchOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<DispatchStatus, string> = {
  PENDING: 'Pending',
  READY_FOR_PICKUP: 'Packed — awaiting pickup',
  PICKED: 'Picked',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
  RETURN_INITIATED: 'Return initiated',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<DispatchStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  PENDING: 'muted',
  READY_FOR_PICKUP: 'warning',
  PICKED: 'warning',
  IN_TRANSIT: 'success',
  OUT_FOR_DELIVERY: 'success',
  DELIVERED: 'success',
  FAILED: 'destructive',
  RETURN_INITIATED: 'warning',
  RETURNED: 'muted',
  CANCELLED: 'muted',
};

/** Human label for a status-history row's `source`. */
const SOURCE_LABEL: Record<string, string> = {
  SYSTEM: 'System',
  INTERNAL: 'Operator',
  PORTAL: 'Portal',
  PARTNER_WEBHOOK: 'Carrier',
  CARRIER_SYNC: 'Carrier sync',
};

/**
 * Forward status progression. A dispatch can also exit via FAILED or
 * RETURN_INITIATED at any non-terminal point; those land in the secondary
 * actions menu.
 */
const FORWARD: Record<DispatchStatus, DispatchStatus | null> = {
  PENDING: 'READY_FOR_PICKUP',
  READY_FOR_PICKUP: 'PICKED',
  PICKED: 'IN_TRANSIT',
  IN_TRANSIT: 'OUT_FOR_DELIVERY',
  OUT_FOR_DELIVERY: 'DELIVERED',
  DELIVERED: null,
  FAILED: null,
  RETURN_INITIATED: 'RETURNED',
  RETURNED: null,
  CANCELLED: null,
};

export function DispatchDetailSheet({ dispatch, open, onOpenChange }: Props) {
  const qc = useQueryClient();

  // Re-fetch canonical detail so partner / status updates from elsewhere
  // are reflected on open.
  const detailQuery = useQuery({
    queryKey: ['dispatch', 'detail', dispatch?.id],
    queryFn: () => dispatchService.get(dispatch!.id),
    enabled: !!dispatch && open,
    // `initialData` is the list row — the BE list query returns it
    // WITHOUT detail-only relations (statusHistory). `initialDataUpdatedAt:
    // 0` marks it stale so the sheet still paints instantly from the list
    // row but always refetches the full detail; without it, staleTime
    // treats the partial row as fresh and the Status timeline never loads.
    initialData: dispatch ?? undefined,
    initialDataUpdatedAt: 0,
    staleTime: 5_000,
  });
  const current = detailQuery.data ?? dispatch;

  const partnersQuery = useQuery({
    queryKey: ['dispatch', 'partners'],
    queryFn: () => dispatchService.listPartners(),
    enabled: open,
    staleTime: 60_000,
  });
  const partners = partnersQuery.data ?? [];

  // Editable shipping / partner / tracking fields. Initialised from the
  // current order when the dialog opens or the detail re-fetches; dirty
  // changes are committed via Save.
  const [partnerId, setPartnerId] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [eta, setEta] = useState('');

  useEffect(() => {
    if (!current) return;
    setPartnerId(current.partnerId ?? '');
    setTrackingNumber(current.trackingNumber ?? '');
    setExternalRef(current.externalDispatchRef ?? '');
    setEta(current.expectedDeliveryAt ? current.expectedDeliveryAt.slice(0, 10) : '');
  }, [current]);

  const isDirty = useMemo(() => {
    if (!current) return false;
    return (
      partnerId !== (current.partnerId ?? '') ||
      trackingNumber !== (current.trackingNumber ?? '') ||
      externalRef !== (current.externalDispatchRef ?? '') ||
      eta !== (current.expectedDeliveryAt ? current.expectedDeliveryAt.slice(0, 10) : '')
    );
  }, [current, partnerId, trackingNumber, externalRef, eta]);

  const saveMutation = useMutation({
    mutationFn: () =>
      dispatchService.update(current!.id, {
        partnerId: partnerId || null,
        trackingNumber: trackingNumber.trim() || undefined,
        externalDispatchRef: externalRef.trim() || undefined,
        expectedDeliveryAt: eta ? new Date(eta).toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success('Dispatch updated');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save dispatch'),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      status,
      reason,
      forcePartial,
    }: {
      status: DispatchStatus;
      reason?: string;
      forcePartial?: boolean;
    }) =>
      dispatchService.updateStatus(current!.id, { status, reason, forcePartial }),
    onSuccess: (_d, vars) => {
      toast.success(`Dispatch moved to ${STATUS_LABEL[vars.status]}`);
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update status'),
  });

  // Carrier reconcile — pull the live status from the partner's
  // tracking API. The recovery path when a carrier webhook is missed.
  const syncMutation = useMutation({
    mutationFn: () => dispatchService.syncTracking(current!.id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      if (current && updated.status !== current.status) {
        toast.success(`Synced — dispatch is now ${STATUS_LABEL[updated.status]}`);
      } else {
        toast.success('Already up to date with the carrier');
      }
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Carrier sync failed'),
  });

  // Sh-scanverify — Pack & verify scan mutation. Sends the scanned
  // SKU to the BE; the response re-hydrates the dispatch with updated
  // scannedQuantity counts. On success we don't toast.success (would
  // be noisy when the operator is rapidly scanning a 30-item box) —
  // just invalidate so the panel re-paints.
  const scanMutation = useMutation({
    mutationFn: (sku: string) => dispatchService.scan(current!.id, { sku }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Scan failed'),
  });

  const unscanMutation = useMutation({
    mutationFn: (itemId: string) =>
      dispatchService.unscan(current!.id, { itemId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not un-scan'),
  });

  // Re-attempt the carrier AWB booking after a failed attempt — the
  // BE runs it in the background, so we invalidate after a short delay
  // to let the new outcome land.
  const retryAwbMutation = useMutation({
    mutationFn: () => dispatchService.retryAwb(current!.id),
    onSuccess: () => {
      toast.success('Retrying carrier booking — the result will appear shortly.');
      setTimeout(() => qc.invalidateQueries({ queryKey: ['dispatch'] }), 2500);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Retry failed'),
  });

  // Manual / retry — request the courier pickup. The BE awaits the
  // carrier call, so the returned dispatch reflects the outcome.
  const schedulePickupMutation = useMutation({
    mutationFn: () => dispatchService.schedulePickup(current!.id),
    onSuccess: (d) => {
      toast.success(
        d.pickupScheduledAt
          ? 'Courier pickup scheduled.'
          : `Pickup not scheduled: ${d.pickupError ?? 'unknown error'}`,
      );
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not schedule pickup'),
  });

  // NDR action — re-attempt delivery or RTO on a failed dispatch. The
  // status change arrives later via the carrier webhook, so we just
  // surface the ack and re-fetch shortly after.
  const ndrMutation = useMutation({
    mutationFn: (action: 'reattempt' | 'rto') =>
      dispatchService.ndrAction(current!.id, action),
    onSuccess: (r, action) => {
      toast.success(
        r.message ??
          (action === 'rto'
            ? 'Return to origin requested.'
            : 'Re-attempt requested.'),
      );
      setTimeout(() => qc.invalidateQueries({ queryKey: ['dispatch'] }), 2500);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'NDR action failed'),
  });

  // MOCK-carrier simulator — operator pushes one of the lifecycle
  // statuses to the same /integrations/tracking/:partnerId/webhook a
  // real carrier hits. Flows through the production ingress so every
  // downstream side-effect (status history, SO mirror, settle-on-
  // pickup, outbound webhook fan-out, observability capture) fires
  // exactly as it would for Shiprocket / DHL. Visible only when the
  // dispatch is wired to the MOCK adapter.
  const simulateMutation = useMutation({
    mutationFn: (nextStatus: DispatchStatus) =>
      dispatchService.simulateCarrierWebhook(current!.partnerId!, {
        awb: current!.trackingNumber!,
        status: nextStatus,
        location: 'Simulator',
        reason: `Mock carrier → ${STATUS_LABEL[nextStatus]}`,
      }),
    onSuccess: (_d, status) => {
      toast.success(`Mock carrier: ${STATUS_LABEL[status]}`);
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Mock carrier push failed'),
  });

  // Label generation + print. Idempotent on the dispatch order id, so
  // calling createLabel twice returns the same label. We chain
  // create → fetch printable HTML → render in new window.
  const printMutation = useMutation({
    mutationFn: async () => {
      const label = await dispatchService.createLabel(current!.id, {});
      const printable = await dispatchService.printLabel(label.id);
      return printable;
    },
    onSuccess: (printable) => {
      const opened = printHtmlInWindow(printable.html, { width: 600, height: 800 });
      if (!opened) {
        toast.error('Popup blocked. Allow popups to print labels.');
        return;
      }
      toast.success('Label rendered');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not generate label'),
  });

  const status = current?.status ?? 'PENDING';
  const next = FORWARD[status];
  const isTerminal = ['DELIVERED', 'RETURNED'].includes(status);
  const canPrint = !!current?.partnerId && !!current?.trackingNumber;

  // Carrier-tracked = a real (non-MANUAL) provider with a live AWB. For
  // these the carrier's tracking webhooks drive the status, so a manual
  // forward move would fight the feed — and can desync it (a later
  // carrier update then fails the state machine and is dropped). MANUAL
  // partners have no API, so the operator stays the source of truth.
  const dispatchPartner = current?.partnerId
    ? partners.find((p) => p.id === current.partnerId)
    : undefined;
  const carrierTracked =
    !!dispatchPartner &&
    dispatchPartner.provider !== 'MANUAL' &&
    !!current?.trackingNumber;
  const [overrideOpen, setOverrideOpen] = useState(false);

  // Controlled tab so the header CTA can jump the operator straight to
  // the Pack & verify panel (which lives on the Lines tab).
  const [activeTab, setActiveTab] = useState('logistics');

  // Pack & verify progress — drives the header CTA. While the dispatch
  // is PENDING the real next action is to scan the package, not to
  // "Mark ready for pickup" (the BE rejects that until all units are
  // scanned). Only once every unit is scanned does the header flip to
  // the status-advance button.
  const packItems = current?.items ?? [];
  const totalUnits = packItems.reduce((s, it) => s + Number(it.quantity), 0);
  const scannedUnits = packItems.reduce(
    (s, it) => s + Number(it.scannedQuantity),
    0,
  );
  const needsPackVerify = status === 'PENDING' && totalUnits > 0;
  const fullyScanned = totalUnits > 0 && scannedUnits >= totalUnits;

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{current?.dispatchNumber ?? 'Dispatch'}</DialogTitle>
            {current && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                >
                  <Truck className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>
                    Dispatch · order {current.salesOrder.orderNumber} · {fmt.relative(current.createdAt)}
                  </DialogEyebrow>
                  <h2 className="font-mono text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {current.dispatchNumber}
                  </h2>
                  <div className="text-[12.5px] text-muted-foreground">
                    Customer:{' '}
                    <span className="font-medium text-foreground">
                      {current.salesOrder.customer.name}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_TONE[status]} className="text-[10px]">
                      {STATUS_LABEL[status]}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.warehouse.code}
                    </Badge>
                    {current.partner && (
                      <Badge variant="muted" className="text-[10px]">
                        {current.partner.name}
                      </Badge>
                    )}
                    {current.trackingNumber && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        {current.trackingNumber}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {/* While PENDING the real next action is Pack & verify
                      (scan the package), not "Mark ready for pickup" —
                      the BE rejects that until every unit is scanned.
                      The CTA reflects that: it jumps to the Lines tab's
                      scan panel until the package is fully scanned. */}
                  {needsPackVerify && !fullyScanned ? (
                    <Button size="sm" onClick={() => setActiveTab('lines')}>
                      <ScanLine className="h-3.5 w-3.5" />
                      Pack &amp; verify ({scannedUnits}/{totalUnits})
                    </Button>
                  ) : (
                    // Forward CTA — hidden once a carrier-tracked dispatch is
                    // past packing: PICKED → DELIVERED arrive via the carrier's
                    // tracking webhooks, so a manual "Mark delivered" here would
                    // fight the feed. The PENDING → ready-for-pickup step stays
                    // (packing the box is the operator's job either way).
                    !isTerminal &&
                    next &&
                    (!carrierTracked || status === 'PENDING') && (
                      <Button
                        size="sm"
                        onClick={() => statusMutation.mutate({ status: next })}
                        disabled={statusMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Mark {STATUS_LABEL[next].toLowerCase()}
                      </Button>
                    )
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => printMutation.mutate()}
                    disabled={!canPrint || printMutation.isPending}
                    title={canPrint ? 'Generate + print label' : 'Set partner and tracking # first'}
                  >
                    <Printer className="h-3.5 w-3.5" />
                    {printMutation.isPending ? 'Generating…' : 'Print label'}
                  </Button>
                  {/* Carriers that host their own invoice alongside the
                      label (DHL CSB-V commercial invoice, Shiprocket tax
                      invoice) — surface it directly. */}
                  {current?.label?.carrierInvoiceUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      title="Open the carrier's hosted invoice"
                    >
                      <a
                        href={current.label.carrierInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        Carrier invoice
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Carrier booking failed — prominent banner + one-click retry.
              Shows while the dispatch is PENDING with a failureReason and
              no tracking number (the AWB fetch was attempted and rejected). */}
          {current &&
            current.status === 'PENDING' &&
            current.failureReason &&
            !current.trackingNumber && (
              <div className="flex items-start gap-3 border-b border-destructive/30 bg-destructive/[0.06] px-7 py-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-destructive">
                    Carrier booking failed — no AWB / tracking number
                  </div>
                  <div className="mt-0.5 text-[12px] text-foreground">
                    {current.failureReason}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Fix the cause above (e.g. add missing HSN codes to the
                    items), then retry. Full call history is on{' '}
                    <span className="font-medium">Webhooks → Outbound API</span>.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => retryAwbMutation.mutate()}
                  disabled={retryAwbMutation.isPending}
                >
                  <Truck className="h-3.5 w-3.5" />
                  {retryAwbMutation.isPending ? 'Retrying…' : 'Retry AWB'}
                </Button>
              </div>
            )}

          {/* Courier pickup not scheduled — retry. Suppressed once the
              parcel is collected/in transit: at that point the pickup
              demonstrably happened and the stored error is stale. */}
          {current &&
            current.pickupError &&
            !current.pickupScheduledAt &&
            !COLLECTED_STATES.has(current.status) && (
            <div className="flex items-start gap-3 border-b border-warning/30 bg-warning/[0.06] px-7 py-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-warning">
                  Courier pickup not scheduled
                </div>
                <div className="mt-0.5 text-[12px] text-foreground">
                  {current.pickupError}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  The parcel won't be collected until a pickup is requested.
                  Retry here, or schedule it from the carrier's dashboard.
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => schedulePickupMutation.mutate()}
                disabled={schedulePickupMutation.isPending}
              >
                <Truck className="h-3.5 w-3.5" />
                {schedulePickupMutation.isPending ? 'Scheduling…' : 'Retry pickup'}
              </Button>
            </div>
          )}

          {/* Courier pickup scheduled — confirmation + carrier pickup date. */}
          {current && current.pickupScheduledAt && (
            <div className="flex items-center gap-2 border-b border-success/30 bg-success/[0.06] px-7 py-2">
              <Truck className="h-3.5 w-3.5 shrink-0 text-success" />
              <div className="text-[12px] text-foreground">
                {current.pickupScheduledDate ? (
                  <>
                    Courier pickup —{' '}
                    <span className="font-semibold">
                      collection on {current.pickupScheduledDate}
                    </span>
                  </>
                ) : (
                  <>Courier pickup requested · {fmt.dateTime(current.pickupScheduledAt)}</>
                )}
              </div>
            </div>
          )}

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="Lines" value={fmt.number(current?.items.length ?? 0)} accent />
            <Stat
              label="Units"
              value={fmt.number((current?.items ?? []).reduce((s, l) => s + Number(l.quantity), 0))}
            />
            <Stat
              label="Picked"
              value={current?.pickedAt ? fmt.relative(current.pickedAt) : '—'}
              muted
            />
            <Stat
              label="Delivered"
              value={current?.deliveredAt ? fmt.relative(current.deliveredAt) : '—'}
              muted
            />
            <Stat
              label="ETA"
              value={current?.expectedDeliveryAt ? fmt.date(current.expectedDeliveryAt) : '—'}
              muted
            />
          </div>

          {/* Carrier-tracked dispatches don't get a "Mark picked /
              in-transit / delivered" CTA because those transitions
              arrive via the carrier's webhooks. Without an explicit
              cue, operators see a button-less header after Mark-as-
              packed and assume something's stalled — surface a clear
              "waiting on carrier, no action needed" banner instead.
              For the MOCK simulator partner we replace the banner with
              an interactive panel — same lifecycle but the operator
              advances it by clicking, since there's no real carrier
              webhook to wait for. See docs/MOCK_CARRIER_GUIDE.md. */}
          {current && carrierTracked && !isTerminal && status !== 'PENDING' && (
            current.partner?.provider === 'MOCK' && current.trackingNumber ? (
              <div className="mx-7 mb-3 space-y-2 rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-3 py-2.5 text-[12px] leading-relaxed text-foreground">
                <div className="flex items-start gap-2.5">
                  <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <div className="flex-1 space-y-0.5">
                    <div className="font-semibold">
                      Mock carrier — simulator mode.
                    </div>
                    <div className="text-muted-foreground">
                      Currently {STATUS_LABEL[status]} · AWB{' '}
                      <span className="font-mono">{current.trackingNumber}</span>. Push a
                      status below to advance the dispatch — the webhook flows through
                      the same production code path a real carrier hits.
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 pl-6">
                  {(
                    [
                      'PICKED',
                      'IN_TRANSIT',
                      'OUT_FOR_DELIVERY',
                      'DELIVERED',
                      'FAILED',
                    ] as DispatchStatus[]
                  )
                    .filter((s) => s !== status)
                    .map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={s === 'FAILED' ? 'outline' : 'secondary'}
                        onClick={() => simulateMutation.mutate(s)}
                        disabled={simulateMutation.isPending}
                        className="h-7 px-2.5 text-[11.5px]"
                      >
                        {STATUS_LABEL[s]}
                      </Button>
                    ))}
                </div>
              </div>
            ) : (
              <div className="mx-7 mb-3 flex items-start gap-2.5 rounded-md border border-info/30 bg-info/[0.05] px-3 py-2 text-[12px] leading-relaxed text-foreground">
                <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-info" />
                <div className="space-y-0.5">
                  <div className="font-semibold">Waiting on {current.partner?.name ?? 'the carrier'} — no action needed.</div>
                  <div className="text-muted-foreground">
                    {STATUS_LABEL[status]}. {current.partner?.name ?? 'The carrier'} will
                    auto-update this dispatch as the parcel is picked, moves through
                    transit, and is delivered. Check back if tracking stalls past 48 hours
                    {current.trackingNumber ? (
                      <>
                        {' '}— AWB <span className="font-mono">{current.trackingNumber}</span>.
                      </>
                    ) : '.'}
                  </div>
                </div>
              </div>
            )
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="logistics" className={DIALOG_TAB_TRIGGER}>
                  <Tags className="h-3.5 w-3.5" />
                  Logistics
                </TabsTrigger>
                <TabsTrigger value="address" className={DIALOG_TAB_TRIGGER}>
                  <MapPin className="h-3.5 w-3.5" />
                  Address
                </TabsTrigger>
                <TabsTrigger value="lines" className={DIALOG_TAB_TRIGGER}>
                  <Package className="h-3.5 w-3.5" />
                  Lines
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                {/* ── Logistics ───────────────────────────────────── */}
                <TabsContent value="logistics" className="m-0 space-y-5">
                  <section className="space-y-3">
                    <SectionHeading
                      title="Partner & tracking"
                      hint="Editable until DELIVERED"
                    />
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-6">
                        <label className="text-[11px] text-muted-foreground" htmlFor="partner">
                          Partner
                        </label>
                        <Select
                          value={partnerId || '__none'}
                          onValueChange={(v) => setPartnerId(v === '__none' ? '' : v)}
                          disabled={isTerminal}
                        >
                          <SelectTrigger id="partner" className="mt-1 h-9">
                            <SelectValue placeholder="No partner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">No partner (manual)</SelectItem>
                            {partners.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                <span>{p.name}</span>
                                <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                                  {p.code} · {p.type}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6">
                        <label className="text-[11px] text-muted-foreground" htmlFor="trackingNumber">
                          Tracking / AWB number
                        </label>
                        <Input
                          id="trackingNumber"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                          placeholder="DTDC123456789"
                          className="mt-1 h-9 font-mono"
                          disabled={isTerminal}
                        />
                      </div>
                      <div className="col-span-6">
                        <label className="text-[11px] text-muted-foreground" htmlFor="externalRef">
                          External reference
                        </label>
                        <Input
                          id="externalRef"
                          value={externalRef}
                          onChange={(e) => setExternalRef(e.target.value)}
                          placeholder="WMS-shipment-1042"
                          className="mt-1 h-9"
                          disabled={isTerminal}
                        />
                      </div>
                      <div className="col-span-6">
                        <label className="text-[11px] text-muted-foreground" htmlFor="eta">
                          Expected delivery
                        </label>
                        <Input
                          id="eta"
                          type="date"
                          value={eta}
                          onChange={(e) => setEta(e.target.value)}
                          className="mt-1 h-9"
                          disabled={isTerminal}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveMutation.mutate()}
                        disabled={!isDirty || saveMutation.isPending || isTerminal}
                      >
                        <Save className="h-3.5 w-3.5" />
                        {saveMutation.isPending ? 'Saving…' : 'Save changes'}
                      </Button>
                    </div>
                  </section>

                  {/* Courier selection — aggregators only, before the AWB is
                      booked. Lets the operator pick a specific carrier with
                      live rate + SLA instead of letting the aggregator route. */}
                  {current &&
                    dispatchPartner &&
                    dispatchPartner.provider !== 'MANUAL' &&
                    !current.trackingNumber && (
                      <>
                        <Separator />
                        <CourierPicker dispatch={current} />
                      </>
                    )}

                  {/* NDR actions — a failed delivery on an API carrier can be
                      re-attempted or sent back (RTO). The carrier feeds the
                      resulting status back through its tracking webhook. */}
                  {current &&
                    status === 'FAILED' &&
                    carrierTracked &&
                    dispatchPartner?.provider === 'SHIPROCKET' && (
                      <>
                        <Separator />
                        <section className="space-y-3">
                          <SectionHeading
                            title="Non-delivery (NDR)"
                            hint="Delivery failed — choose how the carrier should proceed"
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => ndrMutation.mutate('reattempt')}
                              disabled={ndrMutation.isPending}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              Re-attempt delivery
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => ndrMutation.mutate('rto')}
                              disabled={ndrMutation.isPending}
                              className="text-destructive hover:text-destructive"
                            >
                              <Ban className="h-3.5 w-3.5" />
                              Return to origin (RTO)
                            </Button>
                          </div>
                          <p className="text-[11.5px] text-muted-foreground">
                            The status updates automatically once{' '}
                            {dispatchPartner?.name} processes the request.
                          </p>
                        </section>
                      </>
                    )}

                  {!isTerminal && (
                    <>
                      <Separator />
                      <section className="space-y-3">
                        <SectionHeading title="Status transitions" />
                        {carrierTracked ? (
                          // Carrier owns the forward progression — surface that
                          // it is automatic, and keep the exception actions
                          // tucked behind a manual-override disclosure.
                          <div className="space-y-2.5">
                            <div className="flex items-start gap-2 rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[12px] text-muted-foreground">
                              <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-info" />
                              <span>
                                Status updates automatically from{' '}
                                <span className="font-medium text-foreground">
                                  {dispatchPartner?.name}
                                </span>{' '}
                                tracking. If a carrier update was missed, pull
                                the latest now.
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => syncMutation.mutate()}
                              disabled={syncMutation.isPending}
                            >
                              <RefreshCw
                                className={cn(
                                  'h-3.5 w-3.5',
                                  syncMutation.isPending && 'animate-spin',
                                )}
                              />
                              {syncMutation.isPending ? 'Syncing…' : 'Sync from carrier'}
                            </Button>
                            <div>
                              <button
                                type="button"
                                onClick={() => setOverrideOpen((v) => !v)}
                                className="inline-flex items-center gap-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground"
                              >
                                <ChevronRight
                                  className={cn(
                                    'h-3.5 w-3.5 transition-transform',
                                    overrideOpen && 'rotate-90',
                                  )}
                                />
                                Manual override
                              </button>
                              {overrideOpen && (
                                <div className="mt-2 space-y-2 border-l-2 border-border/60 pl-3">
                                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                                    Use only if the carrier integration is unreliable —
                                    a manual move can desync from {dispatchPartner?.name}&apos;s
                                    tracking and cause later updates to be dropped.
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {status !== 'FAILED' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          statusMutation.mutate({
                                            status: 'FAILED',
                                            reason: 'Marked failed by operator',
                                          })
                                        }
                                        disabled={statusMutation.isPending}
                                      >
                                        <Ban className="h-3.5 w-3.5" />
                                        Mark failed
                                      </Button>
                                    )}
                                    {!['RETURN_INITIATED', 'RETURNED'].includes(status) && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          statusMutation.mutate({
                                            status: 'RETURN_INITIATED',
                                            reason: 'Return started by operator',
                                          })
                                        }
                                        disabled={statusMutation.isPending}
                                      >
                                        Return to sender
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {next && (
                              <Button
                                size="sm"
                                onClick={() => statusMutation.mutate({ status: next })}
                                disabled={statusMutation.isPending}
                              >
                                <Check className="h-3.5 w-3.5" />
                                Move to {STATUS_LABEL[next]}
                              </Button>
                            )}
                            {status !== 'FAILED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  statusMutation.mutate({
                                    status: 'FAILED',
                                    reason: 'Marked failed by operator',
                                  })
                                }
                                disabled={statusMutation.isPending}
                              >
                                <Ban className="h-3.5 w-3.5" />
                                Mark failed
                              </Button>
                            )}
                            {!['RETURN_INITIATED', 'RETURNED'].includes(status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  statusMutation.mutate({
                                    status: 'RETURN_INITIATED',
                                    reason: 'Return started by operator',
                                  })
                                }
                                disabled={statusMutation.isPending}
                              >
                                Return to sender
                              </Button>
                            )}
                          </div>
                        )}
                      </section>
                    </>
                  )}

                  {/* Live status timeline — on the main tab so the parcel's
                      progress (and where it is) is visible the moment the
                      dispatch opens, not buried in the History tab. Shown
                      for terminal dispatches too. */}
                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Status timeline" />
                    <DispatchStatusTimeline
                      history={current?.statusHistory ?? []}
                      loading={detailQuery.isFetching}
                    />
                  </section>

                  {current?.trackingScans && current.trackingScans.length > 0 && (
                    <>
                      <Separator />
                      <section className="space-y-3">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                          <SectionHeading title="Carrier scan trail" />
                          {current.lastTrackedAt && (
                            <span className="text-[11px] text-muted-foreground">
                              Last carrier update · {fmt.relative(current.lastTrackedAt)}
                            </span>
                          )}
                        </div>
                        <CarrierScanTrail scans={current.trackingScans} />
                      </section>
                    </>
                  )}

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Audit metadata" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Created" value={fmt.dateTime(current?.createdAt)} />
                      <DetailField label="Updated" value={fmt.relative(current?.updatedAt)} />
                      <DetailField
                        label="Picked"
                        value={current?.pickedAt ? fmt.dateTime(current.pickedAt) : '—'}
                      />
                      <DetailField
                        label="Delivered"
                        value={current?.deliveredAt ? fmt.dateTime(current.deliveredAt) : '—'}
                      />
                      <DetailField
                        label="Expected delivery"
                        value={
                          current?.expectedDeliveryAt
                            ? fmt.dateTime(current.expectedDeliveryAt)
                            : '—'
                        }
                      />
                      {current?.failureReason && (
                        <DetailField label="Failure reason" value={current.failureReason} />
                      )}
                    </div>
                  </section>

                  {!canPrint && (
                    <div className="flex items-center gap-2 rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[12px] text-muted-foreground">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-info" />
                      Set a partner and tracking number, then save — that unlocks label
                      generation and printing.
                    </div>
                  )}
                </TabsContent>

                {/* ── Address ─────────────────────────────────────── */}
                <TabsContent value="address" className="m-0 space-y-3">
                  <SectionHeading title="Shipping address" />
                  <div className="rounded-lg border border-border/70 bg-card p-4 text-[13px] leading-relaxed">
                    {current && (current.shippingAddressLine1 || current.shippingCity) ? (
                      <>
                        {current.shippingContactName && (
                          <div className="font-semibold text-foreground">
                            {current.shippingContactName}
                          </div>
                        )}
                        {current.shippingContactPhone && (
                          <div className="font-mono text-[11.5px] text-muted-foreground">
                            {current.shippingContactPhone}
                          </div>
                        )}
                        <div className="mt-2 text-foreground">
                          {[
                            current.shippingAddressLine1,
                            current.shippingAddressLine2,
                            current.shippingCity,
                            current.shippingState,
                            current.shippingPostalCode,
                            current.shippingCountry,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </>
                    ) : (
                      <div className="text-muted-foreground">No shipping address recorded.</div>
                    )}
                  </div>

                  {current?.notes && (
                    <>
                      <Separator />
                      <section className="space-y-2">
                        <SectionHeading title="Notes" />
                        <p className="text-[13px] leading-relaxed text-foreground">{current.notes}</p>
                      </section>
                    </>
                  )}
                </TabsContent>

                {/* ── Lines ───────────────────────────────────────── */}
                <TabsContent value="lines" className="m-0 space-y-4">
                  {current && status === 'PENDING' && current.items.length > 0 && (
                    <PackAndVerifyPanel
                      items={current.items}
                      onScan={(sku) => scanMutation.mutate(sku)}
                      onUnscan={(itemId) => unscanMutation.mutate(itemId)}
                      onMarkReady={(forcePartial) =>
                        statusMutation.mutate({
                          status: 'READY_FOR_PICKUP',
                          forcePartial,
                        })
                      }
                      isScanning={scanMutation.isPending}
                      isMarkingReady={statusMutation.isPending}
                    />
                  )}

                  <SectionHeading
                    title="Line items"
                    hint={`${current?.items.length ?? 0} line${current?.items.length === 1 ? '' : 's'}`}
                  />
                  {!current || current.items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      No line items.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border/70">
                      <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        <div className="col-span-7">Item</div>
                        <div className="col-span-5 text-right">Quantity</div>
                      </div>
                      {current.items.map((l) => (
                        <DispatchLineRow key={l.id} line={l} />
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── History (read-only metadata for now) ────────── */}
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

/**
 * Dispatch line row with bundle expansion (Slice 17). When the line's
 * item is a bundle, we fetch its components and render the picker
 * breakdown indented under the bundle row so the warehouse operator
 * knows what to physically pull. Components are computed by the
 * inventory-side bundle definition (Slice 12); the actual stock
 * decrement happens against the components at PICKED time (Slice 12b).
 */
function DispatchLineRow({ line }: { line: DispatchOrderItem }) {
  const itemQuery = useQuery({
    queryKey: ['inventory', 'item', line.itemId],
    queryFn: () => inventoryService.get(line.itemId),
    staleTime: 60_000,
  });
  const item = itemQuery.data;
  const isBundle = item?.isBundle ?? false;

  const componentsQuery = useQuery({
    queryKey: ['inventory', 'bundle-components', line.itemId],
    queryFn: () => inventoryService.listBundleComponents(line.itemId),
    enabled: isBundle,
  });
  const components = componentsQuery.data ?? [];

  const lineQty = Number(line.quantity);

  return (
    <div className="border-b border-border/60 last:border-0">
      <div className="grid grid-cols-12 items-center gap-3 px-4 py-2.5 text-[12.5px]">
        <div className="col-span-7 min-w-0">
          <div className="flex items-center gap-2">
            <div className="truncate font-medium text-foreground">{line.name}</div>
            {isBundle && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wide text-primary">
                bundle
              </span>
            )}
          </div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{line.sku}</div>
        </div>
        <div className="col-span-5 text-right font-mono tabular-nums">
          {fmt.number(lineQty)}
        </div>
      </div>

      {/* Bundle expansion — picker breakdown */}
      {isBundle && components.length > 0 && (
        <div className="mb-2 ml-7 rounded-md border-l-2 border-primary/30 bg-muted/20 px-3 py-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Pick these components
          </div>
          <div className="space-y-1">
            {components.map((c) => {
              const totalNeeded = Number(c.quantity) * lineQty;
              return (
                <div
                  key={c.id}
                  className="flex items-center justify-between text-[11.5px]"
                >
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-mono text-[10.5px] text-muted-foreground">
                      {c.component.sku}
                    </span>
                    <span className="ml-2 text-foreground">{c.component.name}</span>
                  </div>
                  <div className="font-mono tabular-nums">
                    {fmt.number(totalNeeded)}{' '}
                    <span className="text-[10px] text-muted-foreground">
                      {c.component.unitOfMeasure}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            Stock decrement happens on these components at PICKED time.
          </div>
        </div>
      )}

      {/* Bundle still loading definitions */}
      {isBundle && componentsQuery.isLoading && (
        <div className="mb-2 ml-7 px-3 text-[10.5px] text-muted-foreground">
          Loading components…
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Sh-scanverify — Pack & verify panel rendered above the line-items
 * list while the dispatch is PENDING. Operator scans (or types) each
 * SKU; we increment scannedQuantity server-side; when every line is
 * fully scanned the "Mark ready for pickup" button enables.
 *
 * Force-partial option is there for the split-pickup edge case (e.g.
 * one of three boxes is going first). Audit-logged on the BE.
 */
function PackAndVerifyPanel({
  items,
  onScan,
  onUnscan,
  onMarkReady,
  isScanning,
  isMarkingReady,
}: {
  items: DispatchOrderItem[];
  onScan: (sku: string) => void;
  onUnscan: (itemId: string) => void;
  onMarkReady: (forcePartial: boolean) => void;
  isScanning: boolean;
  isMarkingReady: boolean;
}) {
  const [scanInput, setScanInput] = useState('');
  const [confirmPartial, setConfirmPartial] = useState(false);

  const totalUnits = items.reduce((s, it) => s + Number(it.quantity), 0);
  const scannedUnits = items.reduce((s, it) => s + Number(it.scannedQuantity), 0);
  const allScanned = totalUnits > 0 && scannedUnits >= totalUnits;
  const someScanned = scannedUnits > 0 && !allScanned;

  const submit = () => {
    const sku = scanInput.trim();
    if (!sku || isScanning) return;
    onScan(sku);
    setScanInput('');
  };

  return (
    <section className="space-y-3 rounded-lg border border-info/30 bg-info/[0.04] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="space-y-0.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-info">
            Pack & verify
          </div>
          <div className="text-[12.5px] text-foreground">
            Scan each item barcode (or type the SKU) into the field below. Every
            line must be fully scanned before the package can be marked ready
            for pickup.
          </div>
        </div>
        <Badge
          variant={allScanned ? 'success' : someScanned ? 'warning' : 'muted'}
          className="shrink-0 font-mono text-[10.5px]"
        >
          {scannedUnits} / {totalUnits} units
        </Badge>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <ScanLine className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Scan or type SKU and press Enter"
            className="pl-8 font-mono"
            disabled={isScanning}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={submit}
          disabled={isScanning || !scanInput.trim()}
        >
          Scan
        </Button>
      </div>

      <div className="overflow-hidden rounded-md border border-border/70 bg-background">
        {items.map((it) => {
          const qty = Number(it.quantity);
          const scanned = Number(it.scannedQuantity);
          const remaining = Math.max(0, qty - scanned);
          const done = scanned >= qty;
          return (
            <div
              key={it.id}
              className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-3 py-2 text-[12.5px] last:border-0"
            >
              <div className="col-span-5 min-w-0">
                <div className="truncate font-medium text-foreground">{it.name}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{it.sku}</div>
              </div>
              <div className="col-span-3 text-right">
                <span
                  className={cn(
                    'font-mono tabular-nums',
                    done ? 'text-success' : remaining > 0 ? 'text-foreground' : '',
                  )}
                >
                  {fmt.number(scanned)} / {fmt.number(qty)}
                </span>
                {remaining > 0 && (
                  <div className="text-[10.5px] text-muted-foreground">
                    {fmt.number(remaining)} left
                  </div>
                )}
              </div>
              <div className="col-span-2 flex justify-center">
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-success" />
                ) : (
                  <span className="h-4 w-4 rounded-full border border-dashed border-muted-foreground/40" />
                )}
              </div>
              <div className="col-span-2 flex justify-end">
                {scanned > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onUnscan(it.id)}
                    title="Reset scanned count for this item"
                    className="h-7 px-2 text-[11.5px] text-muted-foreground hover:text-destructive"
                  >
                    <XIcon className="h-3 w-3" />
                    Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <p className="text-[11.5px] text-muted-foreground">
          {allScanned
            ? 'All items verified — ready to hand to the courier.'
            : someScanned
              ? `${totalUnits - scannedUnits} unit${totalUnits - scannedUnits === 1 ? '' : 's'} still to scan.`
              : 'No scans yet. Use a barcode scanner or type each SKU manually.'}
        </p>
        <div className="flex items-center gap-2">
          {!allScanned && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmPartial(true)}
              disabled={isMarkingReady}
              className="text-muted-foreground hover:text-destructive"
            >
              Skip + mark partial
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onMarkReady(false)}
            disabled={!allScanned || isMarkingReady}
          >
            <Check className="h-3.5 w-3.5" />
            {isMarkingReady ? 'Marking…' : 'Mark as packed'}
          </Button>
        </div>
      </div>

      {confirmPartial && (
        <div className="rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2 text-[12px]">
          <div className="font-semibold text-[hsl(38_85%_30%)]">
            Mark as packed without scanning every item?
          </div>
          <p className="mt-1 text-muted-foreground">
            Use this only for split pickups. The audit log records who bypassed
            the verification gate.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmPartial(false)}
              disabled={isMarkingReady}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setConfirmPartial(false);
                onMarkReady(true);
              }}
              disabled={isMarkingReady}
            >
              {isMarkingReady ? 'Marking…' : 'Mark partial + ready'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * History tab status timeline — renders the dispatch's transition log
 * (oldest-first) from the detail endpoint. The most recent entry is
 * dotted in the primary colour to mark the current status; each row
 * shows where and why the change happened and who reported it
 * (carrier webhook, operator, system automation).
 */
function DispatchStatusTimeline({
  history,
  loading,
}: {
  history: DispatchStatusEvent[];
  loading: boolean;
}) {
  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center text-[12.5px] text-muted-foreground">
        {loading ? 'Loading timeline…' : 'No status changes recorded yet.'}
      </div>
    );
  }
  return (
    <ol className="pl-0.5">
      {history.map((h, i) => {
        const last = i === history.length - 1;
        return (
          <li key={`${h.toStatus}-${h.occurredAt}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-background',
                  last ? 'bg-primary' : 'bg-border',
                )}
              />
              {!last && <span className="w-px flex-1 bg-border" />}
            </div>
            <div className={cn('min-w-0', last ? 'pb-1' : 'pb-4')}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[12.5px] font-semibold text-foreground">
                  {STATUS_LABEL[h.toStatus]}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {fmt.dateTime(h.occurredAt)}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                {h.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {h.location}
                  </span>
                )}
                {h.reason && <span>{h.reason}</span>}
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wide">
                  {SOURCE_LABEL[h.source] ?? h.source}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Carrier scan trail — the granular, carrier-reported movement (newest
 * first) from the latest tracking webhook. Distinct from the internal
 * status timeline above: this shows every DC scan the carrier pushed
 * (e.g. multiple "Received at DC" hops that all map to one IN_TRANSIT),
 * so the operator sees exactly where the parcel is without leaving the
 * app or logging into the carrier dashboard.
 */
function CarrierScanTrail({ scans }: { scans: TrackingScan[] }) {
  // Newest-first — most operators want the latest movement on top.
  const ordered = [...scans].reverse();
  return (
    <ol className="pl-0.5">
      {ordered.map((s, i) => {
        const last = i === ordered.length - 1;
        return (
          <li key={`${s.occurredAt}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'mt-1 h-2 w-2 shrink-0 rounded-full ring-2 ring-background',
                  i === 0 ? 'bg-primary' : 'bg-border',
                )}
              />
              {!last && <span className="w-px flex-1 bg-border" />}
            </div>
            <div className={cn('min-w-0', last ? 'pb-1' : 'pb-3.5')}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                {s.status && (
                  <span className="text-[12px] font-semibold text-foreground">
                    {s.status}
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">
                  {fmt.dateTime(s.occurredAt)}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                {s.activity && <span className="text-foreground/80">{s.activity}</span>}
                {s.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {s.location}
                  </span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Courier selection (aggregators). Before the AWB is booked the operator
 * can pull the route's serviceable carriers — each with rate, SLA days,
 * and rating — and pin one. The chosen courier_id is passed through to
 * AWB assignment. Empty list ⇒ the partner auto-routes (e.g. a non-
 * serviceability provider); we say so rather than showing a picker.
 */
const SELECTION_METHOD_LABEL: Record<string, string> = {
  TIER_AUTO: 'Auto-optimized',
  FIXED_PARTNER: 'Fixed partner',
  OPERATOR: 'Operator pick',
  CARRIER_AUTO: 'Carrier auto-routed',
  RULE_BASED: 'Routing rule',
  COMMITTED: 'Checkout commitment',
};

const SERVICE_TIER_LABEL: Record<string, string> = {
  STANDARD: 'Standard',
  EXPRESS: 'Express',
};

function CourierPicker({ dispatch }: { dispatch: DispatchOrder }) {
  const qc = useQueryClient();
  const [couriers, setCouriers] = useState<ServiceableCourier[] | null>(null);

  const fetchMutation = useMutation({
    mutationFn: () => dispatchService.listCouriers(dispatch.id),
    onSuccess: (rows) => setCouriers(rows),
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not fetch couriers'),
  });

  const selectMutation = useMutation({
    mutationFn: (c: ServiceableCourier) =>
      dispatchService.selectCourier(dispatch.id, {
        courierId: c.courierId!,
        courierName: c.courierName,
        etaDays: c.estimatedDeliveryDays,
        rate: c.rate,
      }),
    onSuccess: () => {
      toast.success('Courier selected');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not select courier'),
  });

  const selected = dispatch.label;
  const hasSelection = !!selected?.selectedCourierId;

  // Delivery promise frozen at checkout. When committedEdd is set, the
  // customer was given a date for a specific courier — fulfilment must
  // honour it (selectionMethod COMMITTED) or the booking is held for the
  // operator (the failure banner above carries the escalation reason).
  const so = dispatch.salesOrder;
  const tierLabel = so?.selectedServiceTier
    ? (SERVICE_TIER_LABEL[so.selectedServiceTier] ?? so.selectedServiceTier)
    : null;
  const committedEdd = so?.committedEdd ?? null;
  const hasCommitment = !!committedEdd || !!so?.committedCourierId;

  return (
    <section className="space-y-3">
      <SectionHeading
        title="Courier selection"
        hint="Pick a carrier before the AWB is booked"
      />

      {hasCommitment && (
        <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[12.5px]">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="font-medium text-foreground">Delivery commitment</span>
            {tierLabel && (
              <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium text-foreground ring-1 ring-inset ring-border">
                {tierLabel}
              </span>
            )}
            {committedEdd && (
              <span className="text-muted-foreground">
                · promised by{' '}
                <span className="font-medium text-foreground">{fmt.date(committedEdd)}</span>
              </span>
            )}
          </div>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Frozen at checkout — fulfilment ships the committed courier or holds for you to
            rebook. Booking a different/slower courier would break the customer’s promise.
          </p>
        </div>
      )}

      {hasSelection && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/20 bg-success/5 px-3 py-2 text-[12.5px]">
          <Check className="h-3.5 w-3.5 shrink-0 text-success" />
          <span className="font-medium text-foreground">
            {selected!.selectedCourierName ?? selected!.selectedCourierId}
          </span>
          {selected!.selectedCourierEtaDays != null && (
            <span className="text-muted-foreground">
              · {selected!.selectedCourierEtaDays}-day SLA
            </span>
          )}
          {selected!.selectedCourierRate != null && (
            <span className="text-muted-foreground">
              · {fmt.money(Number(selected!.selectedCourierRate), 'INR')}
            </span>
          )}
        </div>
      )}

      {/* Why this courier — the resolver's decision, so it's not a black box. */}
      {selected?.selectionTrace && (
        <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2.5 text-[12px]">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              How it was chosen
            </span>
            {selected.selectionMethod && (
              <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium text-foreground ring-1 ring-inset ring-border">
                {SELECTION_METHOD_LABEL[selected.selectionMethod] ?? selected.selectionMethod}
              </span>
            )}
          </div>
          {selected.selectionTrace.reason && (
            <p className="text-muted-foreground">{selected.selectionTrace.reason}</p>
          )}
          {selected.selectionTrace.candidates &&
          selected.selectionTrace.candidates.length > 0 ? (
            <div className="mt-2 space-y-0.5">
              {selected.selectionTrace.candidates.map((c, i) => (
                <div
                  key={`${c.partner}-${c.courier}-${i}`}
                  className={`flex items-center gap-2 rounded px-2 py-1 text-[11.5px] ${
                    c.chosen ? 'bg-success/10 text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {c.chosen ? (
                    <Check className="h-3 w-3 shrink-0 text-success" />
                  ) : (
                    <span className="h-3 w-3 shrink-0" />
                  )}
                  <span className="font-medium">{c.courier}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{c.partner}</span>
                  <span className="ml-auto tabular-nums">
                    {c.rate != null ? fmt.money(Number(c.rate), 'INR') : '—'}
                    {c.etaDays != null ? ` · ${c.etaDays}d` : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      <Button
        size="sm"
        variant="outline"
        onClick={() => fetchMutation.mutate()}
        disabled={fetchMutation.isPending}
      >
        <Truck className="h-3.5 w-3.5" />
        {fetchMutation.isPending
          ? 'Checking couriers…'
          : hasSelection
            ? 'Change courier'
            : 'Check couriers'}
      </Button>

      {couriers !== null && couriers.length === 0 && !fetchMutation.isPending && (
        <p className="text-[12px] text-muted-foreground">
          This partner auto-routes couriers — no manual selection needed.
        </p>
      )}

      {couriers && couriers.length > 0 && (
        <div className="space-y-1.5">
          {couriers.map((c) => {
            const isSel = !!c.courierId && c.courierId === selected?.selectedCourierId;
            return (
              <button
                key={c.courierId ?? c.serviceLabel}
                type="button"
                onClick={() => selectMutation.mutate(c)}
                disabled={selectMutation.isPending || !c.courierId}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-[12.5px] transition-colors',
                  isSel
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border/60 hover:border-border',
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  {isSel ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  ) : (
                    <Truck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate font-medium text-foreground">
                    {c.courierName ?? c.serviceLabel}
                  </span>
                  {c.isRecommended && (
                    <Badge variant="muted" className="text-[10px]">
                      Recommended
                    </Badge>
                  )}
                  {c.isCod && (
                    <Badge variant="muted" className="text-[10px]">
                      COD
                    </Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3 text-[11.5px] text-muted-foreground">
                  {c.estimatedDeliveryDays != null && (
                    <span>{c.estimatedDeliveryDays}-day</span>
                  )}
                  {c.rating != null && <span>★ {c.rating.toFixed(1)}</span>}
                  {c.rate != null && (
                    <span className="font-medium text-foreground">
                      {fmt.money(c.rate, 'INR')}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
